/**
 * El fondo del ingreso: dos manos enguantadas limpiando las esquinas de la
 * tarjeta, en holograma de puntos (CB-23, rediseño 8-sep-2026).
 *
 * ── Por qué existe ──────────────────────────────────────────────────────────
 *
 * La tarjeta del login es de vidrio, y el vidrio solo se lee como vidrio
 * cuando hay algo variado detrás que desenfocar. Esto es ese algo.
 *
 * ── Qué cambió, y por qué (dueño, 8-sep-2026) ───────────────────────────────
 *
 * Antes orbitaban cuatro implementos sueltos por los bordes de la pantalla.
 * El pedido fue otro: **una mano con un guante sosteniendo un cepillo
 * limpiando una esquina, y un plumero en la otra**, con el movimiento
 * «accurate o real a como lo haría un humano». Eso obligó a tres cambios de
 * fondo respecto de la versión de los implementos:
 *
 *   1. **La mano es un modelo anatómico real**, no geometría paramétrica. Una
 *      mano hecha de cajas no se lee como mano a ningún tamaño. La malla
 *      (libigl hand.mesh + hand.tgf, datos de tutorial de Alec Jacobson et
 *      al.) se cierra en un puño fuera del navegador y llega acá como DATOS:
 *      2412 puntos con posición y normal en `/mano-holograma.json`, igual que
 *      `/holo-heightmap.json` alimenta el relieve de Costa Rica en la landing.
 *      Ninguna librería 3D: el mismo `<canvas>` 2D de siempre.
 *   2. **Los puntos ya no se descartan, se atenúan.** La versión anterior
 *      descartaba los puntos de la cara oculta en cada cuadro; al girar la
 *      pieza cruzaban ese umbral de golpe y el dueño lo vio enseguida: «los
 *      puntos aparecen y desaparecen». Ahora "mirar para atrás" es una
 *      variable continua, y se pinta en dos pasadas complementarias — cada
 *      punto se dibuja exactamente una vez por cuadro, así que el parpadeo es
 *      estructuralmente imposible.
 *   3. **El tamaño del punto está atado a la separación entre puntos.** Los
 *      2412 puntos quedan a ~0.008 unidades locales uno de otro; con puntos
 *      más gordos que eso cada uno tapa a sus vecinos y la nube se vuelve una
 *      mancha. Era la razón real de que "pareciera una mano" sin serlo.
 *
 * ── El movimiento ──────────────────────────────────────────────────────────
 *
 * Un `sin(t)` se lee robótico, y hay una razón medible: el pico de velocidad
 * de una sinusoide es 1.571·D/T y el de un movimiento humano es 1.875·D/T —
 * 19 % más alto, con arranque y frenado más marcados. El perfil humano de
 * punto a punto es el de mínimo jerk (Flash & Hogan, 1985), pero por encima de
 * ~1.5 Hz el gesto se vuelve rítmico y sí se aproxima a una sinusoide (Hogan &
 * Sternad, 2007): acá se mezclan los dos según la frecuencia de cada pasada.
 * Encima va lo que separa *restregar* de *oscilar*: la ida hace fuerza y se
 * lleva el 60 % del ciclo, la vuelta es más rápida; la muñeca sigue a la
 * traslación con ~60 ms de retraso; y la herramienta **encara la superficie**
 * — sobre el borde de arriba las cerdas apuntan abajo, sobre el borde derecho
 * el cepillo se acuesta y apuntan a la izquierda, girando en el vértice.
 *
 * ── Restricciones que definen la forma del código (heredadas) ──────────────
 *
 * · **No puede retrasar el formulario.** `null` en el primer render; el canvas
 *   monta en un efecto, y los puntos llegan por `fetch` después de eso.
 * · **Si algo falla, el login sigue.** Todo va en `try/catch`, el `fetch` tiene
 *   su `.catch`, y el peor caso es el degradado de `.auth-stage`.
 * · `prefers-reduced-motion` congela el movimiento: un cuadro y sin bucle.
 * · Sin librerías: proyección 3D a mano en un `<canvas>` 2D.
 */

import { useEffect, useRef, useState } from 'react';

interface Pt3 { x: number; y: number; z: number; nx: number; ny: number; nz: number; w?: number }
interface Vista { W: number; H: number; cx: number; cy: number; s: number }
interface Hebra { pts: Pt3[]; stroke: string; lw: number }
interface Ensamble { nubes: Pt3[][]; hebras: Hebra[] }

const TILT = 0.96;
const cosT = Math.cos(TILT);
const sinT = Math.sin(TILT);
const PERSP = 0.55;

/**
 * Tamaño del punto, relativo a la escala del dibujo. Es EL parámetro de esta
 * pieza: los 2412 puntos de la mano quedan a ~0.008 unidades locales uno de
 * otro, y un punto más gordo que esa separación tapa a sus vecinos. Subirlo
 * convierte la mano en una nube; bajarlo la vuelve polvo.
 */
const PUNTO = 0.006;
/** Separación con la que se muestrean las superficies de las herramientas.
 *  Tiene que parecerse a la de la mano o una pieza sale granulosa al lado de
 *  otra sólida y el conjunto deja de leerse como un solo holograma. */
const PASO = 0.0095;

/* ── Proyección ──────────────────────────────────────────────────────────── */

function project(p: Pt3, yaw: number, v: Vista) {
  const cy = Math.cos(yaw); const sy = Math.sin(yaw);
  const x1 = p.x * cy - p.y * sy;
  const y1 = p.x * sy + p.y * cy;
  const yv = y1 * cosT - p.z * sinT;
  const depth = y1 * sinT + p.z * cosT;
  const persp = 1 / (1 + depth * PERSP);
  return { sx: v.W * v.cx + x1 * v.s * persp, sy: v.H * v.cy + yv * v.s * persp, persp };
}

function colorDe(lit: number): [number, number, number] {
  const k = Math.max(0, Math.min(1, lit));
  return [Math.round(40 + 160 * k), Math.round(96 + 136 * k), Math.round(148 + 107 * k)];
}

/* ── Muestreo de superficies ─────────────────────────────────────────────── */

/** Perímetro de una elipse (Ramanujan). Hace falta para repartir los puntos de
 *  cada anillo: usar `2πr` con el semieje mayor sobre una cabeza chata pide el
 *  triple de puntos de los que entran. */
function perimetro(a: number, b: number) {
  return Math.PI * (3 * (a + b) - Math.sqrt(Math.max(0, (3 * a + b) * (a + 3 * b))));
}

function puntosCilindro(z0: number, z1: number, r0: number, r1: number, paso: number): Pt3[] {
  const pts: Pt3[] = [];
  const filas = Math.max(2, Math.ceil(Math.abs(z1 - z0) / paso));
  // La normal de un cono no es radial pura: se inclina con la pendiente.
  const pend = (r0 - r1) / (z1 - z0); const h = Math.hypot(1, pend);
  const nz = pend / h; const nr = 1 / h;
  for (let i = 0; i <= filas; i++) {
    const t = i / filas; const z = z0 + (z1 - z0) * t; const r = r0 + (r1 - r0) * t;
    const cols = Math.max(6, Math.ceil((2 * Math.PI * r) / paso));
    for (let j = 0; j < cols; j++) {
      const u = (j / cols) * Math.PI * 2 + ((i % 2) * Math.PI) / cols;
      const c = Math.cos(u); const s = Math.sin(u);
      pts.push({ x: c * r, y: s * r, z, nx: c * nr, ny: s * nr, nz });
    }
  }
  return pts;
}

/**
 * Superelipsoide: |x/a|ⁿ + |y/b|ⁿ + |z/c|ⁿ = 1. Con n≈3 da un bloque de cantos
 * redondeados — la forma de la cabeza de un cepillo. Un elipsoide liso se lee
 * como una pastilla.
 */
function puntosSuperelipsoide(cz: number, a: number, b: number, c: number, n: number, paso: number): Pt3[] {
  const pts: Pt3[] = [];
  const sg = (v: number, e: number) => Math.sign(v) * Math.pow(Math.abs(v), e);
  const e = 2 / n;
  const filas = Math.max(4, Math.ceil((Math.PI * (a + b + 2 * c)) / 4 / paso));
  for (let i = 1; i < filas; i++) {
    const v = (i / filas) * Math.PI - Math.PI / 2;
    const cv = Math.cos(v); const sv = Math.sin(v);
    const k = Math.abs(sg(cv, e));
    const cols = Math.max(6, Math.ceil(perimetro(a * k, b * k) / paso));
    for (let j = 0; j < cols; j++) {
      const u = (j / cols) * Math.PI * 2 + ((i % 2) * Math.PI) / cols;
      const x = a * sg(cv, e) * sg(Math.cos(u), e);
      const y = b * sg(cv, e) * sg(Math.sin(u), e);
      const z = c * sg(sv, e);
      const gx = (n * Math.pow(Math.abs(x / a), n - 1) * Math.sign(x)) / a;
      const gy = (n * Math.pow(Math.abs(y / b), n - 1) * Math.sign(y)) / b;
      const gz = (n * Math.pow(Math.abs(z / c), n - 1) * Math.sign(z)) / c;
      const l = Math.hypot(gx, gy, gz) || 1;
      pts.push({ x, y, z: cz + z, nx: gx / l, ny: gy / l, nz: gz / l });
    }
  }
  return pts;
}

/* ── Movimiento humano ───────────────────────────────────────────────────── */

/** Mínimo jerk (Flash & Hogan) mezclado con sinusoide según la frecuencia. */
function perfilHumano(tau: number, hz: number) {
  const t = Math.max(0, Math.min(1, tau));
  const minJerk = 10 * t ** 3 - 15 * t ** 4 + 6 * t ** 5;
  const seno = 0.5 - 0.5 * Math.cos(Math.PI * t);
  const h = Math.max(0, Math.min(1, (hz - 0.8) / 0.7));
  return (1 - h) * minJerk + h * seno;
}

/** Una pasada de ida y vuelta. La ida hace fuerza y se lleva el 60 % del ciclo;
 *  la vuelta es más rápida. Esa asimetría separa restregar de oscilar. */
function pasada(p: number, hz: number) {
  const D = 0.6; const ida = p < D;
  const tau = ida ? p / D : (p - D) / (1 - D);
  const s = perfilHumano(tau, ida ? hz : hz * (D / (1 - D)));
  return ida ? s : 1 - s;
}

interface Vec2 { x: number; y: number }
interface Rutina {
  esquina: Vec2; dirA: Vec2; dirB: Vec2;
  alcance: number; duracion: number; hz: number; fase: number;
  giroA: number; giroB: number;
  /** Aire real disponible, en píxeles, en la dirección "hacia afuera" de cada
   *  borde — ver conAfuera(). En una tarjeta que ocupa casi todo el ancho del
   *  teléfono, `margenX` (el margen lateral) es chico y fijo; `margenY` (el
   *  margen arriba/abajo de una tarjeta centrada) suele sobrar. Medidos cada
   *  cuadro contra la tarjeta real, no supuestos. */
  margenX: number; margenY: number;
  /** Escala y giro de cámara del dibujo. Solo los usa el plumero: el abanico se
   *  ancla en el puño, y para saber dónde cae el puño hay que proyectarlo. */
  /**
   * Hacia dónde es "afuera" (lejos del vidrio). Por defecto se deriva del OTRO
   * borde, el que no se está recorriendo — que es lo correcto mientras la rutina
   * dobla en la esquina y pasa de un borde al otro. Pero si las dos direcciones
   * son la misma, porque la herramienta trabaja UN SOLO borde, esa derivación
   * apunta a lo largo del borde en vez de alejarse de él, y hay que decirlo.
   */
  fuera?: Vec2;
  escala?: number; yaw?: number;
  /** Cuánto pesa el retraso de muñeca que se deriva de la velocidad del punto
   *  de contacto. El cepillo lo usa entero (1). El plumero casi no: su giro ya
   *  está diseñado en `giroExtra`, y dejar el término entero lo duplicaría. */
  retraso?: number;
}
interface Paso {
  x: number; y: number; mezcla: number; vertice?: number;
  /** Giro propio del gesto, sumado al que ya da `mezcla` (rezago y sobrepaso
   *  del plumero). El cepillo no lo usa. */
  giroExtra?: number;
  /** Cuánto se abre el penacho: 1 en reposo, <1 peinado por el aire, >1
   *  esponjado al frenar. El cepillo no lo usa. */
  apertura?: number;
}
type PosFn = (t: number, cfg: Rutina) => Paso;

/**
 * Limpiar una esquina, en tres fases — porque nadie barre *a través* de una
 * esquina: se acerca por un borde, la restriega, y sale por el otro.
 *   A (0–38 %)   pasadas por el borde A, acercándose, con la amplitud
 *                achicándose:  L(d) = L₀·(0.30 + 0.70·min(1, d))
 *   B (38–62 %)  vueltas cortas y rápidas en el vértice
 *   C (62–100 %) pasadas saliendo por el borde B, recuperando amplitud
 *
 * El punto de contacto (x,y acá) ES, por construcción, la yema/las cerdas —
 * z=0 en el espacio local de la herramienta es literalmente "la superficie
 * que se limpia" (ver `cepilloSolido`/`plumeroSolido`). Eso importa porque
 * es tentador desplazar este punto hacia afuera para separar la herramienta
 * de la tarjeta — y una versión anterior lo hacía, con HACIA_AFUERA=0.22
 * (~30px a escala de tarjeta real). Se veía bien en escritorio, pero en el
 * teléfono el dueño lo vio y avisó, con una captura real: «las hebras que
 * limpian no tocan el borde... deberían tocarlo justo, apenas». Tenía razón
 * — mover ESTE punto no separa "la mano" de la tarjeta, separa LA PUNTA que
 * se supone que la está tocando. La separación que sí hacía falta (mano y
 * mango visibles, no tapados) ya la daba el giro (`giroA`/`giroB`, que
 * inclina la herramienta) sin tocar este punto: el puño, más lejos de z=0,
 * se proyecta lejos del ancla solo por la rotación — a la misma distancia
 * relativa sea cual sea la posición del ancla.
 *
 * Por eso HACIA_AFUERA quedó chico: un roce, no una separación. Con 0.03 el
 * peor caso (medido en pantalla) es un contacto de un par de píxeles — se
 * lee como que la cerda toca el vidrio, no que flota al lado.
 *
 * El acotado por eje (`margenX`/`margenY`, más abajo) es de una vuelta
 * anterior — cuando el desplazamiento era grande, hacía falta para no salir
 * de la pantalla en un teléfono (margen lateral ~24px fijos, no escalan con
 * la tarjeta) sin desperdiciar el margen vertical, que casi siempre sobra.
 * Con HACIA_AFUERA=0.03 casi nunca llega a exigirlo, pero se deja: si el
 * número vuelve a crecer por otra razón, la protección sigue ahí.
 */
const HACIA_AFUERA = 0.03; // fracción de `alcance` — un roce, no una separación
const AIRE_USADO = 0.5;    // del margen real disponible en cada eje (ver nota arriba)
/* Compartido por las dos herramientas: el punto de contacto se acota al aire
   real, por eje. Sacado de `posicionEsquina` para que la coreografía del
   plumero (más abajo) lo use exactamente igual — es lo que ya se verificó
   que no se sale de pantalla ni deja de tocar el vidrio. */
function conAfuera(p: Vec2, fx: number, fy: number, alcance: number, margenX: number, margenY: number, levante = 1): Vec2 {
  const l = Math.hypot(fx, fy) || 1; const m = alcance * HACIA_AFUERA * levante;
  const capX = margenX * AIRE_USADO; const capY = margenY * AIRE_USADO;
  const ox = (fx / l) * m; const oy = (fy / l) * m;
  return {
    x: p.x + Math.sign(ox) * Math.min(Math.abs(ox), capX),
    y: p.y + Math.sign(oy) * Math.min(Math.abs(oy), capY),
  };
}
function posicionEsquina(t: number, cfg: Rutina): Paso {
  const ciclo = ((((t + cfg.fase) / cfg.duracion) % 1) + 1) % 1;
  const { esquina, dirA, dirB, alcance, hz, margenX, margenY } = cfg;
  function tramo(fase01: number, dir: Vec2, acercando: boolean) {
    const pasadas = 3;
    const q = fase01 * pasadas; const idx = Math.floor(q); const dentro = q - idx;
    const avance = (idx + dentro) / pasadas;
    const dist = acercando ? 1 - avance : avance;
    const amp = alcance * (0.3 + 0.7 * Math.min(1, dist));
    const centro = alcance * 0.28 * dist + amp * 0.5;
    const d = centro + amp * (pasada(dentro, hz) - 0.5);
    return { x: esquina.x + dir.x * d, y: esquina.y + dir.y * d };
  }
  const fA = cfg.fuera ?? { x: -dirB.x, y: -dirB.y };
  const fV = cfg.fuera ?? { x: -(dirA.x + dirB.x), y: -(dirA.y + dirB.y) };
  const fB = cfg.fuera ?? { x: -dirA.x, y: -dirA.y };
  if (ciclo < 0.38) return { ...conAfuera(tramo(ciclo / 0.38, dirA, true), fA.x, fA.y, alcance, margenX, margenY), mezcla: 0 };
  if (ciclo < 0.62) {
    const p = (ciclo - 0.38) / 0.24; const a = p * Math.PI * 2 * 4; const r = alcance * 0.1;
    const base = {
      x: esquina.x + (dirA.x + dirB.x) * 0.5 * alcance * 0.12 + Math.cos(a) * r,
      y: esquina.y + (dirA.y + dirB.y) * 0.5 * alcance * 0.12 + Math.sin(a) * r * 0.7,
    };
    return {
      ...conAfuera(base, fV.x, fV.y, alcance, margenX, margenY),
      vertice: p,
      // Es acá donde la herramienta gira del borde A al borde B: en el
      // vértice, mientras restriega, no antes ni después.
      mezcla: p * p * (3 - 2 * p),
    };
  }
  return { ...conAfuera(tramo((ciclo - 0.62) / 0.38, dirB, false), fB.x, fB.y, alcance, margenX, margenY), mezcla: 1 };
}

/**
 * El plumero ABANICA. No restriega —eso es el cepillo— y tampoco barre lento
 * —eso fue la versión anterior—: el dueño lo pidió así, «tipo abanico, como si
 * se estuviera ventilando una esquina y ventilando los márgenes... una sacudida
 * corta con un poco de movida».
 *
 * ── La regla que hace que no se confunda con el cepillo ─────────────────────
 *
 * Las dos herramientas están en pantalla AL MISMO TIEMPO, una en cada esquina,
 * comparables de un vistazo. Y esto es una nube de puntos: casi todo lo que
 * separa dos gestos en la vida real —el aire que mueven, la presión, la
 * profundidad— acá no se ve. Lo que sí sobrevive a un dibujo de puntos es quién
 * se queda quieto:
 *
 *     EL CEPILLO desliza la mano MIENTRAS oscila.
 *     EL PLUMERO clava la mano y abanica; y cuando se traslada, no abanica.
 *
 * Exclusión mutua por fase, no por gradiente. Es la única diferencia que se lee
 * de lejos — y es, además, lo que hace un abanico de verdad: el pivote está
 * DENTRO de la mano, y lo que barre es el penacho.
 *
 * ── Por qué el ancla es el puño y no la punta ───────────────────────────────
 *
 * `dibujarEnsamble` rota la herramienta alrededor de su origen local, que es
 * z=0, o sea LA PUNTA. Rotar así deja la punta clavada y manda el puño a
 * describir el arco: eso es una palanca apoyada en el vidrio, no un abanico.
 * Para abanicar hay que invertirlo — anclar el PUÑO y DERIVAR la punta — y para
 * eso hay que saber dónde cae el puño en pantalla, que es lo que resuelve
 * `desplazamientoPuno`.
 *
 * Y hay un premio grande. Con el puño anclado en la banda de abajo, la mano
 * queda dentro de la pantalla y fuera de la silueta de la tarjeta en TODAS las
 * estaciones (medido: x de 30 a 170 en un teléfono de 375). Anclando la punta
 * no había manera: sobre el borde lateral el puño se proyecta a la izquierda
 * del contacto, y ahí solo hay 24 px. Por eso el plumero ya no trabaja el borde
 * lateral — no es preferencia, es que no hay lugar para la mano. El dueño pidió
 * lo mismo por el lado estético: protagonismo abajo y en la esquina.
 *
 * ── De dónde salen los números ──────────────────────────────────────────────
 *
 * De buscar el gesto, y después de descartar la mitad de lo que encontré. Las
 * referencias de abanicar no se ponen de acuerdo ni en la frecuencia (1.1 Hz
 * contra 2.5 contra 4-7) ni en la amplitud (±30° contra ±12°, cada una
 * acusando a la otra de producir «saludo» o «limpiaparabrisas»). Lo que
 * desempata es esta pantalla:
 *
 * · **Frecuencia**: 2.4 Hz, que es 2.1× la del cepillo (1.15 Hz). En un dibujo
 *   de puntos las diferencias finas de tiempo no se leen —hace falta del orden
 *   del 50 % de diferencia para notar que un movimiento cambió de tempo—, así
 *   que una propuesta de 1.11 Hz habría quedado a un 4 % del cepillo: invisible.
 * · **Nada de alto seco ni de asimetría ida/vuelta.** Suena a vida, pero son
 *   justo las dos firmas del cepillo (`pasada()` usa 60/40) y las de la versión
 *   que el dueño ya rechazó. Acá el ángulo es un seno crudo y simétrico; el
 *   acento va en el penacho.
 * · **Todo lo expresivo va en `giroExtra` y `apertura`**, que son los dos
 *   canales que el cepillo no usa. Lo que se exprese moviendo el punto de
 *   contacto es confundible con el cepillo por construcción.
 */

/** Centro de la nube de la mano en el espacio local de la herramienta. Está
 *  sobre el eje del mango: el centroide medido de los 2412 puntos da x = −0.004
 *  y |z| = 0.0027 respecto del puño, o sea que tratar el puño como un punto del
 *  eje es exacto, no una aproximación cómoda.
 *
 *  El signo es RESTA porque el plumero usa la mano volteada, y `(x, −y, −z)` le
 *  da vuelta el centroide: antes de voltearla el centro caía en
 *  `PLU.puno + 0.0027` y ahora cae en `PLU.puno − 0.0027`. Quedó sumando por
 *  descuido cuando se corrigió el agarre — 0.0054 de más, que a la escala del
 *  dibujo es cerca de un píxel y por eso no se vio.
 *
 *  ⚠️ Se mueve junto con `PLU.puno`. Ver la nota de `PLU`. */
const PLU_PUNO_Z = 0.385 - 0.0027;

/** Dónde cae el puño en pantalla respecto del punto de contacto, para un giro
 *  dado. Es la misma proyección que hace `project`, resuelta a mano para el
 *  punto (0, 0, PLU_PUNO_Z) del espacio local. Hace falta acá y no solo al
 *  dibujar, porque el abanico se ancla en el puño y la punta se despeja de esta
 *  ecuación. */
function desplazamientoPuno(giro: number, escala: number, yaw: number): Vec2 {
  const X = PLU_PUNO_Z * Math.sin(giro); const Z = PLU_PUNO_Z * Math.cos(giro);
  const x1 = X * Math.cos(yaw); const y1 = X * Math.sin(yaw);
  const yv = y1 * cosT - Z * sinT;
  const persp = 1 / (1 + (y1 * sinT + Z * cosT) * PERSP);
  return { x: x1 * escala * persp, y: yv * escala * persp };
}

/** El giro con el que la punta queda lo más lejos del puño hacia arriba — o
 *  sea, lo más cerca del borde de la tarjeta. Es el centro del abanico: el
 *  instante de cada pasada en que las hebras rozan el vidrio. Sale de maximizar
 *  la componente vertical de `desplazamientoPuno` y no depende de la escala,
 *  solo de la inclinación de la cámara, así que sirve igual en teléfono y en
 *  escritorio. */
const PLU_GIRO_ROCE = -2.84;

/**
 * Cuánto baja el dibujo del plumero POR DEBAJO del punto de contacto, por unidad
 * de escala: el cuelgue del puño más la nube de la mano y el muñón. Medido sobre
 * el canvas real, no estimado — del borde de la tarjeta al punto más bajo que se
 * pinta hay 0.563 de la escala.
 *
 * Importa porque el abanico ancla la mano DEBAJO de la tarjeta, y el aire que
 * queda ahí no escala con la tarjeta: en un teléfono sobran 143 px, pero en
 * escritorio la tarjeta es más alta y quedan 178 px para un dibujo que, a escala
 * de escritorio, pide 178. Sin acotar, la mano se corta contra el fondo — medido:
 * 103 de 145 cuadros. Es la misma trampa de siempre en esta pieza: una medida que
 * escala con el contenido contra un límite que no.
 */
const PLU_CAIDA = 0.563;

/* La velocidad del sacudido. El dueño la movió: 2.4 → 2.23 → «treinta por
   ciento más lenta» (1.56) → «subilo a 1.8». Dice 1.8 porque eso es lo que
   pidió.

   Estuvo en 1.79 unas horas, por un centésimo que yo defendí con una tabla de
   «cada cuánto se repite el gesto» que estaba mal planteada — ver el comentario
   de `PLU_RESPIRO_HZ`, que cuenta el error entero. Recalculado: 1.79 y 1.80 dan
   EXACTAMENTE el mismo resultado en esa prueba. No había nada que defender.

   Queda en 1.57× la frecuencia del cepillo (1.15 Hz), por debajo del 2× que se
   había puesto como piso para que dos tempos se distingan en una nube de puntos.
   O sea que el ritmo casi no los separa. Lo que sí los separa, y sigue intacto,
   es lo otro: el cepillo traslada la mano MIENTRAS oscila, y el plumero nunca
   hace las dos cosas a la vez — sacude quieto, o se corre sin sacudir. Si alguna
   vez se ven «iguales», el número a mirar es ese, no este. */
const PLU_HZ = 1.8;
const PLU_ARCO = 0.3;        // ±rad: 34° de abanicada
const PLU_CICLOS = 4;        // largo del patrón anti-metrónomo, en ciclos
/* Anti-metrónomo: ningún ciclo mide igual que el anterior. */
const PLU_AMPS = [1, 1.06, 0.97, 0.88];
const PLU_PERS = [1, 0.97, 1.03, 1.08];
const PLU_DUR_CICLO = PLU_PERS.map((p) => p / PLU_HZ);
const PLU_FIN_CICLO = PLU_DUR_CICLO.reduce<number[]>((a, d) => [...a, (a[a.length - 1] ?? 0) + d], []);

/**
 * El patrón de 4 ciclos dura ~1.8 s y, sostenido sin parar, repetiría el mismo
 * compás para siempre — que es justo el mecanismo que el anti-metrónomo evita
 * DENTRO del patrón. Así que encima va una respiración lenta del arco, con una
 * frecuencia distinta de la del patrón: recién cuando los dos ritmos vuelven a
 * coincidir se repite el gesto.
 *
 * NO USES ESTO PARA ELEGIR `PLU_HZ`. Acá hubo una versión de este comentario
 * con una tabla de «a tal frecuencia se repite cada tantos segundos», y dos
 * sesiones movimos el número guiándonos por ella. Estaba mal planteada, y así se
 * ve por qué:
 *
 * Como el patrón tiene período P, comparar t con t+kP deja solo la diferencia de
 * la respiración, y la cuenta sale cerrada — la diferencia máxima entre dos
 * pasadas separadas k patrones es  A·R·2·|sin(π·fr·k·P)|.  O sea que todo
 * depende de qué tan cerca de un entero cae `fr·k·P`, que es una lotería de
 * aproximación racional: 1.70 y 1.80 Hz dan resultados que se llevan cuatro
 * veces, y a 2.40 Hz la diferencia en k=4 es 0.699° contra un umbral de 0.7°.
 * Un centésimo de grado cambiaba la respuesta de 7 a 22 segundos. Dos sesiones
 * midiendo lo mismo con umbrales apenas distintos sacaron tablas que se
 * contradecían, y ninguna de las dos estaba mal calculada.
 *
 * Y al hacerlo bien aparece lo que importa: **ninguna frecuencia del rango útil
 * pasa la prueba.** Barriendo 1.2–2.6 Hz de milésima en milésima, la mejor de
 * todas deja 0.488° si se miran k ≤ 20 patrones, y 0.127° si se mira hasta
 * k ≤ 80. Las dos, muy por debajo del umbral.
 *
 * Y ojo con esa ventana, que es la mitad del resultado: dos sesiones comparamos
 * «el mejor valor del rango» sin decir hasta qué k mirábamos —0.46° contra
 * 0.127°— y creímos que discrepábamos. Cada una había barrido una ventana
 * distinta y las dos estaban bien. Un número de estos sin su k no se puede
 * reproducir, que es el mismo error que el umbral de 0.7° en otra forma.
 *
 * Una prueba que reprueba a TODOS los candidatos no está midiendo un defecto de
 * ninguno: está midiendo el umbral que me inventé yo.
 *
 * La razón de fondo es de tamaños. La respiración modula el arco un ±9 %, así
 * que la diferencia máxima IMAGINABLE entre dos pasadas es 3.28° sobre un arco
 * de 18.22° — y el umbral pregunta por diferencias de menos de 0.7°, o sea por
 * el 4 % de un gesto que además se mira desenfocado detrás de un vidrio. Lo que
 * la prueba llama «se repite» no es algo que un ojo pueda ver.
 *
 * Lo que sí es cierto y alcanza: sin la respiración el compás se repetía cada
 * **1.8 s**, que eso sí se ve. La respiración lo rompe, y encima el viaje
 * horizontal mete un tercer ritmo de 18.2 s.
 *
 * Conclusión práctica: `PLU_HZ` es libre. Poné la velocidad que pida el dueño y
 * no recalcules nada. Si alguna vez de verdad se ve repetitivo, el arreglo no es
 * mover la frecuencia sino partir la respiración en dos componentes de
 * frecuencias distintas (probado: el piso pasa de 0 % a 63 % de frecuencias
 * holgadas) — pero hoy no hace falta.
 */
const PLU_RESPIRO_HZ = 0.137;
const PLU_RESPIRO = 0.09;

/**
 * Dónde se para a sacudir, en fracciones de `alcance` desde la esquina.
 *
 * DOS posiciones, no una (dueño, 8-set-2026, después de ver la versión anclada:
 * «podría desplazarse a limpiar o sacudirse en otro punto, un poco más cercano
 * al centro… es un desplazamiento en modo horizontal, pero no cambia el
 * posicionamiento vertical»).
 *
 * El desplazamiento sale gratis y es horizontal PURO por cómo está definida la
 * rutina: `dirA` del plumero es `{x: 1, y: 0}`, así que `gy` no depende de esta
 * fracción — mover la estación mueve la mano solo en X y no la baja ni la sube
 * un píxel. Si algún día alguien le cambia `dirA` al plumero, esta garantía se
 * cae y hay que volver a mirarla.
 *
 * La de abajo es la del rincón, y es la que está medida:
 *
 * Es lo más cerca de la esquina que se puede sin que la mano se salga por la
 * izquierda — medido sobre el canvas, no elegido. La mano no puede acercarse
 * más porque vive DEBAJO de la tarjeta y desplazada hacia la esquina; las que
 * llegan al rincón son las plumas, que en cada pasada del abanico barren hacia
 * adentro del vértice y vuelven: con 0.34 las plumas llegan a 22 px a la
 * IZQUIERDA del borde de la tarjeta, o sea bien dentro del rincón, mientras el
 * puño se queda a 42 px de la esquina y entero en pantalla. Barrido en el
 * canvas real: 0.30 corta la mano en 13 de 105 cuadros; 0.34, en ninguno.
 */
const PLU_ESTACION = 0.34;

/* ⚠️ EL FLECO TENUE DEL PLUMERO LLEGA A x=0, Y ESTÁ BIEN.
 *
 * Si alguien mide el margen al borde izquierdo, lo va a encontrar en cero y va
 * a querer arreglarlo. Antes de tocar nada: **el número depende enteramente del
 * umbral de opacidad que se elija**, y sin decir el umbral no significa nada.
 * Medido en teléfono (375×812) sobre ~40 s de recorrido, con el mismo código
 * en las dos versiones:
 *
 *      umbral α    puño a media caña (45f69fd)   agarre de varita (da37e32)
 *      > 10               0 px                          0 px
 *      > 30               8 px                          4 px
 *      > 60              17 px                          6 px
 *      > 100             22 px                         15 px
 *      > 180             23 px                         17 px
 *
 * O sea: el fleco llega al borde en las DOS, desde antes de bajar la mano por
 * el mango. Lo que se ve —el cuerpo del plumero, α > 100— conserva 15 px. Y a
 * α = 30 sobre este fondo oscuro el punto corre el color unos 3/8/12 por canal:
 * no se distingue del fondo, por eso nadie lo reportó nunca.
 *
 * Si algún día SÍ se ve cortado, el número a mover es `PLU_ESTACION` —dónde se
 * para a sacudir— y no `PLU.puno`, que es dónde agarra la mano y lo pidió el
 * dueño. Bajar `PLU.puno` para ganar margen deshace la varita.
 *
 * (Yo mismo publiqué «60 px de margen» en el mensaje del commit da37e32. Estaba
 * mal: filtraba por luminancia además de por alfa y me quedé con el núcleo denso
 * de la mano. La tabla de arriba es la medición buena.)
 */
/** La segunda, hacia el centro. Más lejos del borde que la primera, así que el
 *  riesgo de que se corte la mano BAJA — el barrido que fijó 0.34 acota el
 *  extremo peligroso, que es el otro. */
const PLU_ESTACION_B = 0.62;
/** Un viaje de ida y vuelta cada ~18 s. Lento a propósito: esto es una mano
 *  que termina de sacudir un rincón y se corre al siguiente, no un péndulo. */
const PLU_VIAJE_HZ = 0.055;
/** Qué fracción del recorrido pasa quieta sacudiendo en cada punta. Con 0.35 en
 *  cada una quedan ~6 s de sacudido y ~2.7 s de traslado: el gesto se lee como
 *  «sacudo, me corro, sacudo» y no como un vaivén continuo. */
const PLU_QUIETO = 0.35;

/** El suavizado de arranque y frenado del traslado. Sin esto la mano sale y
 *  llega de golpe, que es justo lo que delata una animación. */
const suavePaso = (x: number) => x * x * (3 - 2 * x);

/**
 * En qué estación está el plumero en el instante `t`.
 *
 * Quieto en una punta, viaje suave, quieto en la otra, y vuelta. El período no
 * guarda relación armónica con el del sacudido ni con el de la respiración del
 * arco, así que los tres ritmos no vuelven a coincidir.
 */
function estacionEn(t: number): number {
  const u = (((t * PLU_VIAJE_HZ) % 1) + 1) % 1;
  const viaje = (1 - PLU_QUIETO * 2) / 2;
  const p = u < PLU_QUIETO ? 0
    : u < PLU_QUIETO + viaje ? suavePaso((u - PLU_QUIETO) / viaje)
      : u < PLU_QUIETO * 2 + viaje ? 1
        : 1 - suavePaso((u - PLU_QUIETO * 2 - viaje) / viaje);
  return PLU_ESTACION + (PLU_ESTACION_B - PLU_ESTACION) * p;
}
/** Cuánto se despega del vidrio en las inversiones del arco. Chico a propósito:
 *  el dueño ya falló que las hebras tienen que tocar «justo, apenas». */
const PLU_LEVANTE = 0.35;
/** El penacho: florecido, con dos aperturas por cada ciclo del abanico. Dos, no
 *  una: la carga del aire sobre el penacho es la misma en los dos sentidos, y
 *  abrir solo en la ida señaliza fuerza contra algo — o sea restregar. */
const PLU_AP_BASE = 1.2;
const PLU_AP_LATIDO = 0.22;
const PLU_AP_DESFASE = 0.11;  // fracción de ciclo que el penacho llega tarde respecto del mango

const PLU_PATRON = PLU_FIN_CICLO[PLU_CICLOS - 1] ?? 1;

/**
 * El ángulo del abanico: seno crudo, con la amplitud y el período propios de
 * cada ciclo del patrón, y la respiración lenta encima.
 *
 * Toma el tiempo ABSOLUTO y se enrolla sobre el patrón, así que no hay costura:
 * cada ciclo empieza y termina en 0, de modo que el empalme entre uno y otro
 * —y entre una vuelta del patrón y la siguiente— es continuo por construcción.
 */
function anguloAbanico(u: number) {
  const w = ((u % PLU_PATRON) + PLU_PATRON) % PLU_PATRON;
  let i = 0; let t0 = 0;
  while (i < PLU_CICLOS - 1 && w >= t0 + (PLU_DUR_CICLO[i] ?? 0)) { t0 += PLU_DUR_CICLO[i] ?? 0; i++; }
  const p = (w - t0) / (PLU_DUR_CICLO[i] ?? 1);
  const respiro = 1 + PLU_RESPIRO * Math.sin(u * Math.PI * 2 * PLU_RESPIRO_HZ);
  return { giro: PLU_ARCO * (PLU_AMPS[i] ?? 1) * respiro * Math.sin(p * Math.PI * 2), p };
}

/**
 * El plumero sacude en el lugar, sin parar y sin moverse de ahí. Antes recorría
 * cinco estaciones a lo largo del borde, abanicando en cada una y trasladándose
 * entre ellas; el dueño vio ese movimiento, le gustó el sacudido y pidió dejarlo
 * fijo: «¿será posible que solo fijemos de manera constante ese sacudido... sin
 * que haya desplazamiento, muy cerca de la esquina, pero igual permaneciendo con
 * el enfoque abajo?».
 *
 * Sin traslado, la regla de exclusión que separaba este gesto del cepillo se
 * cumple de la forma más fuerte posible: el punto de contacto ya no se desplaza
 * NUNCA a lo largo del borde. Lo único que se mueve es el ángulo del mango, y
 * las plumas detrás.
 *
 * Y como no hay recorrido, tampoco hay ciclo que cerrar: se maneja con el tiempo
 * absoluto y no existe la costura del final de la vuelta.
 */
function posicionEsquinaPlumero(t: number, cfg: Rutina): Paso {
  const { esquina, dirA, dirB, alcance, margenX, margenY } = cfg;
  const escala = cfg.escala ?? 1; const yaw = cfg.yaw ?? 0;
  const r = anguloAbanico(t + cfg.fase);

  /* El puño se ancla donde la punta roza el borde; la punta se deriva del
     ángulo. Ése es todo el abanico. */
  const roce = desplazamientoPuno(cfg.giroA, escala, yaw);
  const off = desplazamientoPuno(cfg.giroA + r.giro, escala, yaw);
  /* La estación se corre con el tiempo, y como `dirA` del plumero es
     `{x: 1, y: 0}` el desplazamiento sale horizontal puro: `gy` no depende de
     ella y la mano no sube ni baja un píxel. */
  const est = estacionEn(t + cfg.fase);
  const gx = esquina.x + dirA.x * alcance * est;
  const gy = esquina.y + dirA.y * alcance * est;
  const contacto = { x: gx + roce.x - off.x, y: gy + roce.y - off.y };

  // En las inversiones del arco la punta se despega un pelo; por el centro roza.
  const levante = 1 + PLU_LEVANTE * Math.sin(r.p * Math.PI * 2) ** 2;
  return {
    ...conAfuera(contacto, -dirB.x, -dirB.y, alcance, margenX, margenY, levante),
    mezcla: 0, giroExtra: r.giro,
    apertura: PLU_AP_BASE + PLU_AP_LATIDO * Math.sin((r.p - PLU_AP_DESFASE) * Math.PI * 2) ** 2,
  };
}

/**
 * Posición + velocidad + giro. La velocidad no es decorativa: de ella salen
 * el arqueo de las cerdas y el rezago de las plumas. `posFn` es lo único que
 * distingue al cepillo (restriega) del plumero (sacude): todo lo demás —el
 * retraso, el giro de borde a borde, el roce contra el vidrio— es la misma
 * función para los dos.
 */
function limpiarEsquina(t: number, cfg: Rutina, posFn: PosFn = posicionEsquina) {
  const DT = 1 / 60; const RETRASO = 0.06;
  const p = posFn(t, cfg);
  const antes = posFn(t - DT, cfg);
  const atrasado = posFn(t - RETRASO, cfg);
  const vx = (p.x - antes.x) / DT; const vy = (p.y - antes.y) / DT;
  const delta = p.x - atrasado.x + (p.y - atrasado.y);
  let giro = Math.max(-0.3, Math.min(0.3, delta * 0.012)) * (cfg.retraso ?? 1);
  if (p.vertice !== undefined) giro += 0.14 * Math.sin(p.vertice * Math.PI);
  const base = cfg.giroA + (cfg.giroB - cfg.giroA) * p.mezcla;
  return { x: p.x, y: p.y, vx, vy, giro: base + giro + (p.giroExtra || 0), apertura: p.apertura ?? 1 };
}

/**
 * Pasa una velocidad de PANTALLA a la dirección local equivalente, para que las
 * cerdas se arqueen hacia donde de verdad quedaría el arrastre. Sin esto el
 * arqueo se ve bien en un tramo del recorrido y al revés en el otro.
 * Se invierten, en orden, la rotación de `ubicar` y la de `project`.
 */
function aLocal(vx: number, vy: number, yaw: number, girar: number) {
  const a = vx; const b = vy / cosT;
  const cy = Math.cos(yaw); const sy = Math.sin(yaw);
  const bx = a * cy + b * sy; const by = -a * sy + b * cy;
  return { x: bx * Math.cos(girar), y: by, z: bx * Math.sin(girar) };
}

/* ── Geometría de las dos herramientas ───────────────────────────────────── */
/* Medidas relativas, con z = 0 en la superficie que se limpia. */
const CEP = {
  cerdas: 0.052, cabezaZ: 0.094, cabezaA: 0.15, cabezaB: 0.056, cabezaC: 0.042,
  cuello0: 0.136, cuello1: 0.222, mango1: 0.505, puno: 0.338,
};
/**
 * El plumero, a lo largo de su eje. `puno` es dónde va el centro de la mano.
 *
 * ⚠️ `puno` y `PLU_PUNO_Z` se mueven JUNTOS. `PLU_PUNO_Z` es el mismo punto
 * resuelto a mano para anclar el dibujo en pantalla; si se cambia uno solo, la
 * mano se corre por el mango pero el plumero se sigue colgando del lugar viejo.
 *
 * `puno` a 0.385 y no en medio del mango (dueño, 8-set-2026, después de ver el
 * agarre corregido): «que se tome más desde el borde que no tiene plumas, como
 * una varita mágica, más sujeta desde la parte de abajo». La mano mide ±0.11 en
 * z, así que a 0.385 los dedos llegan a 0.495 y el mango termina en 0.5 más su
 * casquete — queda agarrado del extremo con un cabo corto asomando, que es lo
 * que se ve cuando alguien sostiene una varita.
 */
const PLU = { collar: 0.212, virola0: 0.18, virola1: 0.222, mango1: 0.5, puno: 0.385 };

export function AuthHologram() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => { setMounted(true); }, []);

  useEffect(() => {
    if (!mounted) return undefined;
    const canvasMaybe = canvasRef.current;
    if (!canvasMaybe || !canvasMaybe.getContext) return undefined;
    const ctxMaybe = canvasMaybe.getContext('2d');
    if (!ctxMaybe) return undefined;
    const canvas: HTMLCanvasElement = canvasMaybe;
    const ctx: CanvasRenderingContext2D = ctxMaybe;

    let raf = 0;
    let vivo = true;
    let onResize: (() => void) | undefined;

    /* La mano llega como datos. Hasta que llegue no se dibuja nada: el fondo
       es el degradado de `.auth-stage`, que ya es un fondo válido. */
    fetch('/mano-holograma.json')
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((crudo: number[][]) => {
        if (!vivo || !Array.isArray(crudo) || crudo.length === 0) return;
        arrancar(crudo);
      })
      .catch(() => { /* sin mano, queda el degradado: el ingreso nunca depende de esto */ });

    function arrancar(crudo: number[][]) {
      try {
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        /* Se valida fila por fila en vez de confiar: es un archivo servido, y
           un `undefined` colado acá se convierte en `NaN` en la proyección y
           en un cuadro en blanco. */
        const MANO: Pt3[] = [];
        for (const r of crudo) {
          const [x, y, z, nx, ny, nz] = r;
          if (x === undefined || y === undefined || z === undefined
            || nx === undefined || ny === undefined || nz === undefined) continue;
          MANO.push({ x, y, z, nx, ny, nz });
        }
        if (MANO.length === 0) return;
        // Radio del mango = el del túnel que forman los dedos enroscados. Sale
        // del mismo preproceso que la nube; si algún día cambia el modelo, este
        // número cambia con él.
        const RADIO_MANGO = 0.0195;

        /**
         * La muñeca se desvanece en vez de terminar cortada: la malla incluye un
         * muñón, y un holograma que termina en un tajo se lee como error.
         *
         * `voltear` da media vuelta a la mano sobre el eje X. Existe porque las
         * dos herramientas NO se agarran igual, y el dueño lo vio: «la mano
         * sujeta al revés el plumero. En el cepillo está correcto, pero cuando
         * se sujeta un plumero para poder limpiar, el pulgar queda arriba, o
         * sea, del lado de las plumas. No se utiliza como un picador de hielo,
         * que es como está sujetado en este momento».
         *
         * Lo medí sobre el modelo antes de tocar nada: de los 2412 puntos, los
         * 261 del pulgar están a z ≈ +0.086 y el resto de la mano a z ≈ −0.007.
         * Como z=0 es la punta de la herramienta y +z se va por el mango, el
         * pulgar estaba mirando al mango. Picador de hielo, tal cual.
         *
         * Es una ROTACIÓN, no un espejo: (x, −y, −z) tiene determinante +1 y
         * sigue siendo la misma mano. Espejar sobre Z la convertiría en la otra
         * mano — el mismo error que ya se cometió una vez con `flip`, cuando
         * dio la herramienta patas arriba en lugar de la mano espejada.
         *
         * Y ojo con el orden: el desvanecido del muñón se calcula con la `y`
         * ORIGINAL. Si se rota primero y se desvanece después, el degradado cae
         * sobre los dedos en vez de sobre la muñeca.
         */
        function manoUbicada(zCentro: number, voltear = false): Pt3[] {
          const Y0 = 0.185; const Y1 = 0.262;
          return MANO.map((p) => {
            const w = 1 - Math.max(0, Math.min(1, (p.y - Y0) / (Y1 - Y0)));
            const q = voltear
              ? { x: p.x, y: -p.y, z: -p.z, nx: p.nx, ny: -p.ny, nz: -p.nz }
              : p;
            return { ...q, z: q.z + zCentro, w: w * w };
          });
        }

        function cepilloSolido(): Pt3[][] {
          const R = RADIO_MANGO;
          return [
            puntosSuperelipsoide(CEP.cabezaZ, CEP.cabezaA, CEP.cabezaB, CEP.cabezaC, 3.4, PASO),
            puntosCilindro(CEP.cuello0, CEP.cuello1, 0.036, R, PASO),
            puntosCilindro(CEP.cuello1, CEP.mango1, R, R * 0.92, PASO),
            puntosSuperelipsoide(CEP.mango1, R * 0.94, R * 0.94, R * 1.5, 2.6, PASO),
            manoUbicada(CEP.puno),
          ];
        }

        /** Las cerdas van en MECHONES, no en peine: un cepillo real tiene grupos
         *  de filamentos plantados en agujeros, y la sombra entre mechón y
         *  mechón es la mitad de la lectura. Se arquean EN CONTRA del
         *  movimiento — una cerda apretada contra el vidrio no lo atraviesa. */
        function cepilloCerdas(empuje: number, lateral: number): Hebra[] {
          const E: Hebra[] = [];
          const filas = 4; const cols = 11; const L = CEP.cerdas; const z0 = CEP.cerdas;
          const arq = Math.max(-1.4, Math.min(1.4, empuje));
          const arqL = Math.max(-1.4, Math.min(1.4, lateral));
          for (let f = 0; f < filas; f++) {
            const ty = f / (filas - 1);
            const y0 = (ty - 0.5) * 2 * CEP.cabezaB * 0.74;
            for (let c = 0; c < cols; c++) {
              const tx = c / (cols - 1);
              const x0 = (tx - 0.5) * 2 * CEP.cabezaA * 0.82;
              // Los mechones del borde son más cortos: la cabeza es un bloque
              // redondeado y las cerdas se plantan sobre esa curva.
              const borde = Math.max(Math.abs(tx - 0.5), Math.abs(ty - 0.5)) * 2;
              const largo = L * (1 - 0.16 * borde * borde);
              for (let k = 0; k < 4; k++) {
                const a = (k / 4) * Math.PI * 2 + (c * 1.7 + f * 0.9);
                const abre = 0.008;
                const dx = Math.cos(a) * abre; const dy = Math.sin(a) * abre * 0.75;
                const ax = arq * 0.026; const ay = arqL * 0.026;
                const zPunta = largo * 0.1 * Math.min(1, Math.abs(arq) + Math.abs(arqL));
                E.push({
                  pts: [
                    { x: x0, y: y0, z: z0, nx: 0, ny: 0, nz: 0 },
                    { x: x0 + dx * 0.45 + ax * 0.3, y: y0 + dy * 0.45 + ay * 0.3, z: z0 - largo * 0.58, nx: 0, ny: 0, nz: 0 },
                    { x: x0 + dx + ax, y: y0 + dy + ay, z: zPunta, nx: 0, ny: 0, nz: 0 },
                  ],
                  stroke: `rgba(168,222,255,${(0.26 + 0.14 * (1 - Math.abs(ty - 0.5) * 2)).toFixed(2)})`,
                  lw: 1,
                });
              }
            }
          }
          return E;
        }

        function plumeroSolido(): Pt3[][] {
          const R = RADIO_MANGO;
          return [
            puntosCilindro(PLU.virola0, PLU.virola1, R * 1.55, R * 1.28, PASO),
            puntosCilindro(PLU.virola1, PLU.mango1, R * 1.06, R * 0.8, PASO),
            puntosSuperelipsoide(PLU.mango1, R * 0.82, R * 0.82, R * 1.6, 2.6, PASO),
            manoUbicada(PLU.puno, true),
          ];
        }

        /** Una pluma no es un pelo: es un raquis con barbas a los dos lados.
         *  Solo el raquis da un plumero de espagueti. Y llegan tarde: el penacho
         *  es liviano, así que arrastra detrás de la mano, más cuanto más lejos
         *  del mango.
         *
         *  `apertura` es lo que hace que se lea como plumas y no como un pompón
         *  rígido: un cuerpo flexible en flujo se reconfigura y se afina (por eso
         *  se cierra en huso mientras barre), y al frenar deja de haber flujo y
         *  la elasticidad lo devuelve, esponjado. Ese ciclo cerrar-abrir es el
         *  detalle que más comunica "plumero" — un penacho que mantiene la misma
         *  silueta todo el ciclo mata el gesto. */
        function plumeroPlumas(arrX: number, arrY: number, apertura: number): Hebra[] {
          const E: Hebra[] = [];
          const N = 17; const PASOS = 6;
          for (let i = 0; i < N; i++) {
            const a = (i / N) * Math.PI * 2 + 0.4;
            const largo = 0.84 + 0.32 * Math.sin(i * 2.3); // penacho disparejo
            const ca = Math.cos(a); const sa = Math.sin(a);
            const raquis: Pt3[] = [];
            for (let k = 0; k <= PASOS; k++) {
              const u = k / PASOS;
              const r = 0.012 + 0.14 * apertura * Math.pow(u, 1.22) * largo;
              const z = PLU.collar - 0.22 * Math.pow(u, 0.86) * largo;
              // El rezago crece con u²: la base sigue a la mano, la punta llega tarde.
              raquis.push({ x: ca * r + arrX * u * u, y: sa * r + arrY * u * u, z: Math.max(0, z), nx: 0, ny: 0, nz: 0 });
            }
            E.push({ pts: raquis, stroke: 'rgba(196,232,255,.42)', lw: 1.1 });
            for (let k = 1; k < PASOS; k++) {
              const u = k / PASOS;
              const p = raquis[k]; const q = raquis[k + 1];
              if (!p || !q) continue;
              const tx = q.x - p.x; const ty = q.y - p.y; const tz = q.z - p.z;
              let px = -ty; let py = tx;
              const pl = Math.hypot(px, py) || 1; px /= pl; py /= pl;
              const bl = Math.sin(u * Math.PI) * 0.042 * largo;
              for (const sgn of [1, -1]) {
                E.push({
                  pts: [
                    { x: p.x, y: p.y, z: p.z, nx: 0, ny: 0, nz: 0 },
                    { x: p.x + px * bl * sgn + tx * 0.5, y: p.y + py * bl * sgn + ty * 0.5, z: p.z + tz * 0.5 - bl * 0.26, nx: 0, ny: 0, nz: 0 },
                  ],
                  stroke: 'rgba(176,220,252,.22)',
                  lw: 1,
                });
              }
            }
          }
          return E;
        }

        let W = 0; let H = 0; let dpr = 1;
        function resize() {
          dpr = window.devicePixelRatio || 1;
          W = canvas.offsetWidth; H = canvas.offsetHeight;
          canvas.width = Math.round(W * dpr);
          canvas.height = Math.round(H * dpr);
        }
        resize();

        /**
         * Los puntos NUNCA se descartan.
         *
         * El parpadeo de la versión anterior era esto: cada cuadro se
         * descartaban los puntos de la cara oculta (`if (n·V >= 0) continue`), y
         * al girar la pieza un punto cruzaba ese umbral de golpe. Acá "mirar
         * para atrás" solo ATENÚA, que además es más fiel a un holograma. Dos
         * pasadas complementarias exactas: cada punto se pinta una vez y solo
         * una, así que no hay umbral que cruzar.
         */
        function dibujarNube(puntos: Pt3[], ubicar: (p: Pt3) => Pt3, yaw: number, vista: Vista, escalaPunto: number) {
          const V = { x: Math.sin(yaw) * sinT, y: Math.cos(yaw) * sinT, z: cosT };
          const L = { x: 0.32, y: -0.58, z: 0.75 };
          function pintar(soloAtras: boolean) {
            for (const src of puntos) {
              const p = ubicar(src);
              const haciaCamara = -(p.nx * V.x + p.ny * V.y + p.nz * V.z);
              const frente = 0.5 + 0.5 * Math.max(-1, Math.min(1, haciaCamara));
              if (soloAtras ? frente >= 0.5 : frente < 0.5) continue;
              const q = project(p, yaw, vista);
              // Un punto detrás del plano de la cámara da `persp` negativo, y un
              // radio negativo hace que arc() TIRE — dentro de un
              // requestAnimationFrame eso no es un punto feo, es el holograma
              // entero apagado.
              if (!(q.persp > 0)) continue;
              const lit = Math.max(0, p.nx * L.x + p.ny * L.y + p.nz * L.z);
              const c = colorDe(lit * frente);
              const w = src.w === undefined ? 1 : src.w;
              const rad = (0.42 + lit * 0.85) * (0.5 + 0.5 * frente) * q.persp * escalaPunto;
              const alpha = (0.1 + 0.62 * frente * (0.45 + 0.55 * lit)) * w;
              if (alpha < 0.012) continue;
              ctx.fillStyle = `rgba(${c[0]},${c[1]},${c[2]},${alpha.toFixed(3)})`;
              // Por debajo de ~1.6 px un círculo y un cuadrado son el mismo
              // pixelaje, y fillRect no arma un path: son ~7500 puntos por
              // cuadro entre las dos manos, y ahí la diferencia se siente.
              if (rad < 1.6) ctx.fillRect(q.sx - rad, q.sy - rad, rad * 2, rad * 2);
              else { ctx.beginPath(); ctx.arc(q.sx, q.sy, rad, 0, 6.2832); ctx.fill(); }
            }
          }
          pintar(true);
          pintar(false);
        }

        function hebra(pts: Pt3[], yaw: number, vista: Vista, stroke: string, lw: number) {
          ctx.beginPath();
          let primero = true;
          for (const p of pts) {
            const q = project(p, yaw, vista);
            if (primero) { ctx.moveTo(q.sx, q.sy); primero = false; } else ctx.lineTo(q.sx, q.sy);
          }
          ctx.lineWidth = lw;
          ctx.strokeStyle = stroke;
          ctx.stroke();
        }

        /** `zurda` espeja sobre X. Solo hay un modelo, y es una mano derecha;
         *  espejarlo sobre Z no da una mano izquierda, da la misma mano derecha
         *  con la herramienta patas arriba. */
        function dibujarEnsamble(geom: Ensamble, vista: Vista, yaw: number, girar: number, zurda: boolean, escalaPunto: number) {
          const cs = Math.cos(girar); const sn = Math.sin(girar);
          const fx = zurda ? -1 : 1;
          const ubicar = (p: Pt3): Pt3 => {
            const px = p.x * fx; const pnx = p.nx * fx;
            return {
              x: px * cs + p.z * sn, y: p.y, z: -px * sn + p.z * cs,
              nx: pnx * cs + p.nz * sn, ny: p.ny, nz: -pnx * sn + p.nz * cs,
              w: p.w,
            };
          };
          for (const nube of geom.nubes) dibujarNube(nube, ubicar, yaw, vista, escalaPunto);
          for (const e of geom.hebras) hebra(e.pts.map(ubicar), yaw, vista, e.stroke, e.lw);
        }

        /* Las nubes se arman una vez; solo las hebras se rehacen por cuadro,
           porque dependen de la velocidad. */
        const solidoCepillo = cepilloSolido();
        const solidoPlumero = plumeroSolido();

        const YAW_CEP = -0.5; const YAW_PLU = -0.75;

        function frame(tMs: number) {
          const t = reduceMotion ? 3.2 : tMs / 1000;
          ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
          ctx.clearRect(0, 0, W, H);

          /* Las esquinas salen de la tarjeta REAL, medida en cada cuadro. Es lo
             que mantiene las manos pegadas al vidrio en cualquier tamaño de
             pantalla, y lo que hace que sigan al teclado en un teléfono. */
          const stage = canvas.parentElement;
          const tarjeta = stage ? stage.querySelector('.auth-card') : null;
          if (!stage || !tarjeta) return;
          const caja = stage.getBoundingClientRect();
          const tj = tarjeta.getBoundingClientRect();
          const left = tj.left - caja.left; const right = tj.right - caja.left;
          const top = tj.top - caja.top; const bottom = tj.bottom - caja.top;
          const lado = right - left;
          if (lado <= 0) return;

          /* En un teléfono la tarjeta ocupa casi todo el ancho y quedan ~24 px
             de margen a los lados: la mano —que es lo que hay que ver— corre
             riesgo de salirse de la pantalla. Achicar la escena entera cuando
             el margen es chico (`compacto`) es lo que evita eso.
             OJO, error que ya se cometió acá: una versión anterior además
             corría el PUNTO DE CONTACTO hacia adentro de la tarjeta
             ("pisa el vidrio"), pensando que eso ayudaba. No ayuda — ese punto
             es la yema de las cerdas (ver `posicionEsquina`), y correrlo hacia
             adentro la mete bajo el vidrio traslúcido, que la desvanece: es
             literalmente el defecto que se estaba corrigiendo, reaparecido
             por otra puerta. El punto de contacto SIEMPRE es la esquina real
             de la tarjeta (más el roce de `HACIA_AFUERA`) — nunca se corre
             hacia adentro. Lo único que cambia en compacto es la escala. */
          const margen = Math.min(left, W - right);
          const compacto = margen < lado * 0.28;
          const escala = lado * (compacto ? 0.62 : 0.8);
          const escalaPunto = escala * PUNTO;

          /* Esquina superior derecha: el cepillo. Trabaja el BORDE SUPERIOR y su
             esquina, y nada más — igual que el plumero trabaja el inferior y la
             suya. Los bordes laterales quedaron fuera del campo de uso de las
             dos herramientas, por pedido del dueño y porque además ahí no hay
             lugar: en un teléfono el margen lateral es de ~24px fijos (no escala
             con la tarjeta) y el puño, que se proyecta lejos del ancla por el
             giro, no entra. Arriba y abajo, en cambio, sobra aire.

             Por eso `dirB` es la misma dirección que `dirA`: la rutina de tres
             fases sigue igual —entra restregando con pasadas que se achican,
             trabaja el vértice, y sale con pasadas que crecen— pero las dos
             pasadas ocurren sobre el mismo borde. Y por eso hace falta `fuera`
             explícito: la derivación por defecto usa el OTRO borde, que acá ya
             no existe.

             El movimiento en sí no se tocó: mismo `alcance`, `duracion`, `hz`,
             `fase` y `giroA` que la versión que el dueño aprobó. Y como ya no
             hay tramo lateral, tampoco hace falta la pose de compacto que
             existía solo para ese tramo (`giroBCep`). */
          const cep = limpiarEsquina(t, {
            esquina: { x: right, y: top }, dirA: { x: -1, y: 0 }, dirB: { x: -1, y: 0 },
            fuera: { x: 0, y: -1 },
            alcance: lado * 0.42, duracion: 17.6, hz: 1.15, fase: 0,
            // Cerdas para abajo, mango inclinado hacia afuera: derecho, el puño
            // queda detrás de la tarjeta y el vidrio lo desvanece. La misma pose
            // en las dos pasadas, porque ahora las dos son el mismo borde.
            giroA: 0.42, giroB: 0.42,
            margenX: W - right, margenY: top,
          });
          // La velocidad viene en px/s: a unidades locales antes de deformar nada.
          const vCep = aLocal(cep.vx / escala, cep.vy / escala, YAW_CEP, cep.giro);
          dibujarEnsamble(
            { nubes: solidoCepillo, hebras: cepilloCerdas(-vCep.x * 0.9, -vCep.y * 0.9) },
            { W, H, cx: cep.x / W, cy: cep.y / H, s: escala },
            YAW_CEP, cep.giro, false, escalaPunto,
          );

          // Mismo problema que arriba, del otro lado: en compacto, el tramo
          // izquierdo (`giroB`) es el que no tiene aire. -2.7 (cerca de
          // `giroA`, no de -π/2+0.1) mide ~11px de proyección lateral en vez
          // de ~66px.

          // Esquina inferior izquierda: el plumero, que ataca desde afuera —
          // sobre el borde de abajo el mango cae hacia la izquierda y el penacho
          // sube contra el vidrio. Ya no cruza al borde izquierdo: ahí el puño
          // se proyecta hacia afuera y en un teléfono solo hay 24 px de margen.
          // Si el aire de abajo no alcanza para el cuelgue de la mano, el plumero
          // se dibuja más chico en vez de salirse. En el teléfono no llega a
          // exigirlo; en escritorio recorta ~9 %.
          const escPlu = Math.min(escala * 1.04, Math.max(60, (H - bottom - 16) / PLU_CAIDA));
          const plu = limpiarEsquina(t, {
            esquina: { x: left, y: bottom }, dirA: { x: 1, y: 0 }, dirB: { x: 0, y: -1 },
            alcance: lado * 0.38, duracion: 21.6, hz: 0.9, fase: 4.2,
            // El abanico gira alrededor de esta pose, que es la del roce. Ya no
            // hay un `giroB` distinto: como el plumero no cruza al borde lateral,
            // tampoco necesita la pose de compacto que hacía falta para ese cruce.
            giroA: PLU_GIRO_ROCE, giroB: PLU_GIRO_ROCE,
            margenX: left, margenY: H - bottom,
            escala: escPlu, yaw: YAW_PLU, retraso: 0.15,
          }, posicionEsquinaPlumero);
          const vPlu = aLocal(plu.vx / escPlu, plu.vy / escPlu, YAW_PLU, plu.giro);
          dibujarEnsamble(
            { nubes: solidoPlumero, hebras: plumeroPlumas(-vPlu.x * 0.045, -vPlu.y * 0.045, plu.apertura) },
            { W, H, cx: plu.x / W, cy: plu.y / H, s: escPlu },
            YAW_PLU, plu.giro, true, escPlu * PUNTO,
          );
        }

        frame(0);

        if (!reduceMotion) {
          const loop = (tMs: number) => {
            if (!vivo) return;
            frame(tMs);
            raf = requestAnimationFrame(loop);
          };
          raf = requestAnimationFrame(loop);
        }

        onResize = () => { resize(); frame(0); };
        window.addEventListener('resize', onResize);
      } catch {
        // Cualquier falla deja el degradado de `.auth-stage`: el ingreso jamás
        // depende de este dibujo.
      }
    }

    return () => {
      vivo = false;
      if (raf) cancelAnimationFrame(raf);
      if (onResize) window.removeEventListener('resize', onResize);
    };
  }, [mounted]);

  if (!mounted) return null;

  return <canvas ref={canvasRef} className="auth-holo" aria-hidden="true" />;
}
